import { useState, useEffect, useContext } from 'react';
import { Form, Row, Col, Button, Card, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import SingleDatePicker from '../../../../components/common/SingleDatePicker/SingleDatePicker';
import TimeSlotSlider from '../../common/TimeSlotSlider';
import AutoCompleteInput from '../../../../components/common/AutoComplete/AutoCompleteInput';

import { useGetRestaurantHolidays, useGetRestaurantOutlets } from '../../../../APIs/common';
import { useAddBooking, useGetServicePeriods } from '../../../../APIs/BookingManagement/booking';
import { ConfigContext } from '../../../../contexts/ConfigContext';
import { reactSelectDark } from '../../../../styles/reactSelectDarkTheme';
import { useGetAvailableTimeSlots } from '../../../../APIs/BookingManagement/booking';
import { notify } from '@common/NotificationService';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import MainCard from '../../../../components/MainCard';
import { toYMD } from '../../../../utils/FormatDate';
import LoyaltyOptIn from '../../common/LoyaltyOptIn';
import AmenityChips from '../../common/AmenityChips';
import './AddNewBooking.css';
import DisabledFieldWrapper from '../../../../components/common/DisabledFieldWrapper/DisabledFieldWrapper';
import { reactSelectLight } from '../../../../styles/reactSelectLightTheme';
import api from '../../../../api/axiosClient';
import usePermission from '../../../../hooks/usePermission';
import {
  AU_PHONE_ERROR,
  EMAIL_ERROR,
  filterEmailInput,
  filterNameInput,
  formatPhoneInput,
  isValidAuPhone,
  isValidEmail,
  localPhoneDigits,
  normaliseName,
  phoneForApi,
  validatePersonName
} from '../../../../utils/validation/customerFields';

function useClickOutside(ref, callback) {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) callback();
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, callback]);
}

export default function AddNewBooking({ inModal = false, onCreated, onCancel }) {
  const navigate = useNavigate();
  const config = useContext(ConfigContext);

  const [city, setCity] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState(null);

  const [location, setLocation] = useState(null);
  const [locationOptions, setLocationOptions] = useState([]);

  const [date, setDate] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [nonWorkingDays, setNonWorkingDays] = useState([]);

  const [guestCount, setGuestCount] = useState('');

  const [servicePeriod, setServicePeriod] = useState(null);
  const [servicePeriodOptions, setServicePeriodOptions] = useState([]);

  const [amenities, setAmenities] = useState([]);
  const [amenitiesOptions, setAmenitiesOptions] = useState([]);

  const [timeSlots, setTimeSlots] = useState([]);
  const [timeSlot, setTimeSlot] = useState(null);

  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  // Ticked by default: staff taking a booking on the phone should not have to
  // remember. Ticking enrols nobody — the counter asks them on the first visit.
  const [joinLoyalty, setJoinLoyalty] = useState(true);
  const [adminComment, setAdminComment] = useState('');
  const [modeOfBooking, setModeOfBooking] = useState('manual');

  const [selectedSlotMeta, setSelectedSlotMeta] = useState(null);

  const [errors, setErrors] = useState({});

  const { outlets, isLoading: isLocationLoading } = useGetRestaurantOutlets({
    location_id: selectedCityId
  });

  const { getRestaurantHolidays } = useGetRestaurantHolidays();
  const { getServicePeriods, isMutating: isServiceLoading } = useGetServicePeriods();
  const { getAvailableTimeSlots, isMutating: isSlotLoading } = useGetAvailableTimeSlots();
  const { addBooking, isMutating: isBookingCreating } = useAddBooking();
  const { has } = usePermission();

  useEffect(() => {
    if (!outlets || outlets.length === 0) return;

    const mapped = outlets.map((o) => ({
      value: o.id,
      label: o.name
    }));

    setLocationOptions((prev) => {
      const same = prev.length === mapped.length && prev.every((p, i) => p.value === mapped[i].value);
      return same ? prev : mapped;
    });
  }, [outlets]);

  useEffect(() => {
    if (!selectedSlotMeta || !guestCount) return;

    if (guestCount > selectedSlotMeta.availableSeats) {
      setErrors((prev) => ({
        ...prev,
        guestCount: `Only ${selectedSlotMeta.availableSeats} seats available for this slot`
      }));
    } else {
      setErrors((prev) => ({ ...prev, guestCount: undefined }));
    }
  }, [selectedSlotMeta, guestCount]);

  const validateForm = () => {
    const newErrors = {};

    if (!city) newErrors.city = 'City is required';
    if (!location) newErrors.location = 'Location is required';
    if (!date) newErrors.date = 'Date is required';

    if (!guestCount || guestCount <= 0) newErrors.guestCount = 'Enter a valid number of guests';

    if (selectedSlotMeta && guestCount > selectedSlotMeta.availableSeats) {
      newErrors.guestCount = `Only ${selectedSlotMeta.availableSeats} seats available for the selected slot`;
    }

    if (!servicePeriod) newErrors.servicePeriod = 'Service period is required';
    // if (amenities.length === 0) newErrors.amenities = 'Select at least one amenity';

    if (!modeOfBooking) newErrors.modeOfBooking = 'Mode of booking is required';
    if (!timeSlot) newErrors.timeSlot = 'Select a time slot';

    // Name, phone and email: the tablet app's rules and messages
    // (utils/validation/customerFields.js).
    const nameError = validatePersonName(customerName, 'Customer name');
    if (nameError) newErrors.customerName = nameError;

    if (!localPhoneDigits(customerPhone.trim())) {
      newErrors.customerPhone = 'Phone number is required';
    } else if (!isValidAuPhone(customerPhone)) {
      newErrors.customerPhone = AU_PHONE_ERROR;
    }

    // Email is optional, but if provided, it must be valid
    if (customerEmail.trim() && !isValidEmail(customerEmail)) {
      newErrors.customerEmail = EMAIL_ERROR;
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleCitySelect = (item) => {
    setCity(item);
    setSelectedCityId(item.value);

    setLocation(null);
    setDate(null);
    setGuestCount('');
    setServicePeriod(null);
    setAmenities([]);
    setTimeSlots([]);
    setTimeSlot(null);
  };

  const handleLocationSelect = async (sel) => {
    setLocation(sel);

    // Reset lower dependencies
    setDate(null);
    setGuestCount('');
    setServicePeriod(null);
    setAmenities([]);
    setAmenitiesOptions([]);
    setTimeSlots([]);
    setTimeSlot(null);

    // The location's Preference Amenities, loaded with the location rather
    // than only after the guest count, so the field can be used straight
    // away. Needs the listing permission; without it the list still arrives
    // with the service periods once the guests are entered.
    if (has('preference_listing')) {
      api
        .get('/amenities/list', { params: { location_id: sel.value, status: 1, per_page: 200 } })
        .then((r) => {
          const list = r?.data?.data?.amenities || [];
          setAmenitiesOptions(list.map((a) => ({ value: a.id, label: a.name })));
        })
        .catch(() => {});
    }

    // Load holidays
    const res = await getRestaurantHolidays(sel.value);
    setHolidays(res.holidays || []);
    setNonWorkingDays(res.nonWorkingDays);
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);

    // Reset
    setGuestCount('');
    setServicePeriod(null);
    setAmenities([]);
    setTimeSlots([]);
    setTimeSlot(null);
  };

  // const handleGuestChange = async (e) => {
  //   const value = e.target.value;
  //   setGuestCount(value);

  //   setServicePeriod(null);
  //   setAmenities([]);
  //   setTimeSlots([]);
  //   setTimeSlot(null);

  //   // Load service periods & preferences
  //   if (location?.value && date && value > 0) {
  //     const res = await getServicePeriods({
  //       location_id: location.value,
  //       date,
  //       no_of_guests: value
  //     });

  //     setServicePeriodOptions(
  //       res.servicePeriods.map((sp) => ({
  //         value: sp.id,
  //         label: sp.name
  //       }))
  //     );

  //     setAmenitiesOptions(
  //       res.preferences.map((p) => ({
  //         value: p.id,
  //         label: p.name
  //       }))
  //     );
  //   }
  // };

  // const handleServiceOrAmenitiesSelected = async () => {
  //   setTimeSlots([]);
  //   setTimeSlot(null);

  //   if (location?.value && date && guestCount > 0 && servicePeriod?.value) {
  //     const res = await getAvailableTimeSlots({
  //       location_id: location.value,
  //       date,
  //       service_period_id: servicePeriod.value,
  //       no_of_guests: guestCount
  //     });

  //     setTimeSlots(
  //       (res.timeSlots || []).map((slot) => ({
  //         label: slot.time,
  //         value: slot.time_24h
  //       }))
  //     );
  //   }
  // };

  const handleGuestChange = async (e) => {
    const value = Number(e.target.value);
    setGuestCount(value);

    // Reset dependent fields
    setServicePeriod(null);
    setAmenities([]);
    setTimeSlots([]);
    setTimeSlot(null);
    setSelectedSlotMeta(null);

    // Slot-based validation
    if (selectedSlotMeta && value > selectedSlotMeta.availableSeats) {
      setErrors((prev) => ({
        ...prev,
        guestCount: `Only ${selectedSlotMeta.availableSeats} seats available for this slot`
      }));
    } else {
      setErrors((prev) => ({ ...prev, guestCount: undefined }));
    }

    if (location?.value && date && value > 0) {
      const res = await getServicePeriods({
        location_id: location.value,
        date,
        no_of_guests: value
      });

      setServicePeriodOptions(
        res.servicePeriods.map((sp) => ({
          value: sp.id,
          label: sp.name
        }))
      );

      // Only when the lookup sent some — an empty answer (no free service
      // period, say) must not wipe the list loaded with the location.
      if (res.preferences?.length) {
        setAmenitiesOptions(
          res.preferences.map((p) => ({
            value: p.id,
            label: p.name
          }))
        );
      }
    }
  };

  const handleServiceOrAmenitiesSelected = async (selectedServicePeriod = servicePeriod, selectedAmenities = amenities) => {
    setTimeSlot(null);

    const servicePeriodId = selectedServicePeriod?.value;

    if (location?.value && date && guestCount > 0 && servicePeriodId) {
      // The times on show stay up until the new ones arrive. Emptying the list
      // first folded away the time slots and the customer lines below them for
      // a moment on every change, which made the New Booking pop-up blink.
      let res;
      try {
        res = await getAvailableTimeSlots({
          location_id: location.value,
          date,
          service_period_id: servicePeriodId,
          no_of_guests: guestCount
        });
      } catch (err) {
        setTimeSlots([]);
        throw err;
      }

      // setTimeSlots(
      //   (res.timeSlots || []).map((slot) => ({
      //     label: slot.time,
      //     value: slot.time_24h
      //   }))
      // );
      setTimeSlots(
        (res.timeSlots || []).map((slot) => ({
          label: slot.time,
          value: slot.time_24h,
          availableSeats: slot.available_seats,
          isFullyBooked: slot.is_fully_booked
        }))
      );
    } else {
      setTimeSlots([]);
    }
  };

  // A badge's ✕: the same as unticking it in the dropdown.
  const removeAmenity = (item) => {
    const next = amenities.filter((a) => a.value !== item.value);
    setAmenities(next);
    handleServiceOrAmenitiesSelected(servicePeriod, next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      // notify.error('Please fix the highlighted errors');
      return;
    }
    const payload = {
      location_id: Number(location?.value),
      service_period_id: servicePeriod?.value,
      date: toYMD(date),
      time: timeSlot,
      no_of_guests: guestCount,
      customer_name: normaliseName(customerName),
      // 0412 345 678, 61… and +61… all go out as 61 and the nine digits.
      customer_phone: phoneForApi(customerPhone),
      customer_email: customerEmail.trim(),
      amenities: amenities.map((a) => a.value),
      internal_notes: adminComment,
      mode_of_booking: modeOfBooking,
      join_loyalty: joinLoyalty,
      auto_confirm: true
    };

    try {
      const res = await addBooking(payload);
      if (res.success) {
        notify.success(res.message || 'Booking created successfully', 'add');
        if (inModal) onCreated?.(res);
        else navigate('/booking-management');
      } else {
        notify.error(res.message || 'Failed to create booking', 'add');
      }
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Something went wrong while creating booking', 'add');
    }
  };

  // One look for every dropdown here. The menus are drawn on the page body so
  // the pop-up never cuts them off.
  const selectStyles = {
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    }),
    menuList: (base) => ({
      ...base,

      maxHeight: 130,
      overflowY: 'auto'
    }),

    control: (base) => ({
      ...base,
      minHeight: 42
    }),
    ...(config.mode === 'dark' ? reactSelectDark : reactSelectLight)
  };
  // Picked preferences as theme-green badges (dark green light, mint dark).
  const amenityStyles = {
    ...selectStyles,
    multiValue: (base) => ({
      ...base,
      backgroundColor: 'var(--yz-accent, #0e4535)',
      borderRadius: 6
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: 'var(--yz-on-accent, #fff)',
      fontWeight: 500
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: 'var(--yz-on-accent, #fff)',
      borderRadius: '0 6px 6px 0',
      ':hover': { backgroundColor: 'rgba(0, 0, 0, 0.18)', color: 'var(--yz-on-accent, #fff)' }
    })
  };
  const menuTarget = typeof document !== 'undefined' ? document.body : null;
  const pickedSlot = timeSlots.find((s) => s.value === timeSlot);

  const form = (
    <Form onSubmit={handleSubmit} className="nb-form yz-list-toolbar">
      {/* Line 1 — where, when, how many, which period and preferences. */}
      <Row className="g-3 nb-line">
        {/* CITY */}
        <Col xl={2} md={4} sm={6} xs={12}>
          <Form.Label>
            City <span className="text-danger">*</span>
          </Form.Label>
          <AutoCompleteInput
            value={city?.label || ''}
            apiEndpoint="/restaurant-location/get-location"
            placeholder="City"
            menuPortal={inModal}
            onChange={(text) => setCity({ label: text, value: text })}
            onSelect={(item) => handleCitySelect({ label: item.name, value: item.id })}
          />
          {errors.city && <div className="text-danger small">{errors.city}</div>}
        </Col>

        <Col xl={2} md={4} sm={6} xs={12}>
          <Form.Label>
            Location <span className="text-danger">*</span>
          </Form.Label>
          <DisabledFieldWrapper disabled={!selectedCityId} reason="Please select a location first">
            <div>
              <Select
                options={locationOptions}
                value={location}
                onChange={handleLocationSelect}
                placeholder="Location"
                isDisabled={!selectedCityId}
                menuPortalTarget={menuTarget}
                styles={selectStyles}
              />
            </div>
          </DisabledFieldWrapper>
          {errors.location && <div className="text-danger small">{errors.location}</div>}
        </Col>

        <Col xl={2} md={4} sm={6} xs={12}>
          <Form.Label>
            Date <span className="text-danger">*</span>
          </Form.Label>
          <DisabledFieldWrapper disabled={!location} reason="Please select a location first">
            <div>
              <SingleDatePicker
                disabled={!location}
                value={date}
                holidays={holidays.map((h) => h.date)}
                onSelect={handleDateSelect}
                useClickOutside={useClickOutside}
                nonWorkingDays={nonWorkingDays}
                placeholder="Date"
                menuPortal={inModal}
              />
            </div>
          </DisabledFieldWrapper>
          {errors.date && <div className="text-danger small">{errors.date}</div>}
        </Col>

        <Col xl={2} md={4} sm={6} xs={12}>
          <Form.Label>
            No. of Guests <span className="text-danger">*</span>
          </Form.Label>
          <DisabledFieldWrapper
            disabled={!location || !date}
            reason={location ? 'Please select a date first' : 'Please select a location first'}
          >
            <div>
              <Form.Control
                type="number"
                value={guestCount}
                onChange={handleGuestChange}
                disabled={!location || !date}
                placeholder="No. of Guests"
              />
            </div>
          </DisabledFieldWrapper>
          {errors.guestCount && <div className="text-danger small">{errors.guestCount}</div>}
        </Col>

        <Col xl={2} md={4} sm={6} xs={12}>
          <Form.Label>
            Service Period <span className="text-danger">*</span>
          </Form.Label>
          <DisabledFieldWrapper
            disabled={!servicePeriodOptions.length}
            reason={location ? 'Please enter guest value' : 'Please select a location first'}
          >
            <div>
              <Select
                options={servicePeriodOptions}
                value={servicePeriod}
                onChange={(sel) => {
                  setServicePeriod(sel);
                  handleServiceOrAmenitiesSelected(sel, amenities);
                }}
                isDisabled={!servicePeriodOptions.length}
                placeholder="Service Period"
                menuPortalTarget={menuTarget}
                styles={selectStyles}
              />
            </div>
          </DisabledFieldWrapper>
          {errors.servicePeriod && <div className="text-danger small">{errors.servicePeriod}</div>}
        </Col>

        <Col xl={2} md={4} sm={6} xs={12}>
          <Form.Label>Preference Amenities</Form.Label>
          <DisabledFieldWrapper disabled={!location} reason="Please select a location first">
            <div className="nb-multi">
              <Select
                isMulti
                options={amenitiesOptions}
                value={amenities}
                onChange={(sel) => {
                  setAmenities(sel);
                  handleServiceOrAmenitiesSelected(servicePeriod, sel);
                }}
                noOptionsMessage={() =>
                  guestCount ? 'No preference amenities for this location' : 'Enter No. of guests to load amenities'
                }
                placeholder="Preference Amenities"
                // The picks are listed under the field as badges instead.
                controlShouldRenderValue={false}
                menuPortalTarget={menuTarget}
                styles={amenityStyles}
              />
            </div>
          </DisabledFieldWrapper>
          <AmenityChips items={amenities} onRemove={removeAmenity} />
        </Col>
      </Row>

      {/* Line 2 — the time, and how the booking came in. */}
      <Row className="g-3 nb-line">
        {timeSlots.length > 0 && (
          <Col xl={9} xs={12}>
            <Form.Label>
              Time Slots <span className="text-danger">*</span>
            </Form.Label>
            <TimeSlotSlider
              slots={timeSlots}
              selected={timeSlot}
              onSelect={(slotValue) => {
                const slotObj = timeSlots.find((s) => s.value === slotValue);

                setTimeSlot(slotValue);
                setSelectedSlotMeta(slotObj);

                // Reset guest error if valid
                if (guestCount && slotObj && guestCount <= slotObj.availableSeats) {
                  setErrors((e) => ({ ...e, guestCount: undefined }));
                }
              }}
            />
            {errors.timeSlot && <div className="text-danger small">{errors.timeSlot}</div>}
          </Col>
        )}

        <Col xl={3} xs={12}>
          <Form.Label>
            Mode of Booking <span className="text-danger">*</span>
          </Form.Label>
          <div className="nb-mode">
            <Form.Check
              inline
              type="radio"
              id="nb-mode-manual"
              label="Manual"
              value="manual"
              checked={modeOfBooking === 'manual'}
              onChange={(e) => setModeOfBooking(e.target.value)}
            />
            <Form.Check
              inline
              type="radio"
              id="nb-mode-phone"
              label="Phone Call"
              value="phone"
              checked={modeOfBooking === 'phone'}
              onChange={(e) => setModeOfBooking(e.target.value)}
            />
          </div>
          {errors.modeOfBooking && <div className="text-danger small">{errors.modeOfBooking}</div>}
        </Col>
      </Row>

      {timeSlots.length > 0 && (
        <>
          {/* Line 3 — the customer. */}
          <Row className="g-3 nb-line">
            <Col xl={3} md={6} xs={12}>
              <Form.Label>
                Customer Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                value={customerName}
                onChange={(e) => {
                  // Only letters, spaces and ' can be typed.
                  setCustomerName(filterNameInput(e.target.value));
                  setErrors((prev) => ({ ...prev, customerName: undefined }));
                }}
                placeholder="Customer Name"
              />
              {errors.customerName && <div className="text-danger small">{errors.customerName}</div>}
            </Col>

            <Col xl={3} md={6} xs={12}>
              <Form.Label>
                Customer Phone <span className="text-danger">*</span>
              </Form.Label>
              {/* The whole number, spaced as it is typed: 0412 345 678,
                  02 9876 5432 or +61 412 345 678. */}
              <Form.Control
                value={customerPhone}
                inputMode="tel"
                onChange={(e) => {
                  setCustomerPhone(formatPhoneInput(e.target.value));
                  setErrors((prev) => ({ ...prev, customerPhone: undefined }));
                }}
                placeholder="Customer Phone"
              />
              {errors.customerPhone && <div className="text-danger small">{errors.customerPhone}</div>}
            </Col>

            <Col xl={3} md={6} xs={12}>
              <Form.Label>Customer Email</Form.Label>
              <Form.Control
                type="email"
                value={customerEmail}
                onChange={(e) => {
                  // No spaces, capped at the RFC limit.
                  setCustomerEmail(filterEmailInput(e.target.value));
                  setErrors((prev) => ({ ...prev, customerEmail: undefined }));
                }}
                placeholder="Customer Email"
              />
              {errors.customerEmail && <div className="text-danger small">{errors.customerEmail}</div>}
            </Col>

            {/* Draws nothing at all unless a programme is actually running.
                The lookup expects the ten-digit 0… form, whichever way the
                number was typed. */}
            <Col xl={3} md={6} xs={12} className="nb-loyalty">
              <LoyaltyOptIn
                phone={localPhoneDigits(customerPhone) ? `0${localPhoneDigits(customerPhone)}` : ''}
                checked={joinLoyalty}
                onChange={setJoinLoyalty}
                compact
              />
            </Col>
          </Row>

          {/* Line 4 — the note for staff. */}
          <Row className="g-3 nb-line">
            <Col xs={12}>
              <Form.Label>Admin Comment</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={adminComment}
                onChange={(e) => setAdminComment(e.target.value)}
                placeholder="Admin Comment"
              />
            </Col>
          </Row>

          {/* Line 5 */}
          {!inModal && (
            <div className="nb-actions">
              <Button type="submit" disabled={isBookingCreating}>
                {isBookingCreating ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  'Create Booking'
                )}
              </Button>
            </div>
          )}
        </>
      )}

      {/* In the pop-up, line 5 is its footer — as the View Booking popup's
          Assign Table: what is being booked on the left, Cancel and the
          main action on the right. */}
      {inModal && (
        <div className="nb-foot">
          <div className="nb-foot__picked">
            {pickedSlot ? (
              <>
                <i className="ph ph-check-circle" />
                <span>
                  <strong>{date}</strong> · {pickedSlot.label} · {guestCount} {Number(guestCount) === 1 ? 'guest' : 'guests'}
                </span>
              </>
            ) : (
              <span className="nb-foot__none">No time selected</span>
            )}
          </div>
          <div className="nb-foot__buttons">
            <button type="button" className="nb-foot__cancel" disabled={isBookingCreating} onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary nb-foot__primary" disabled={isBookingCreating || !timeSlots.length}>
              {isBookingCreating ? <Spinner size="sm" /> : <i className="ph ph-check" />}
              {isBookingCreating ? 'Creating...' : 'Create Booking'}
            </button>
          </div>
        </div>
      )}
    </Form>
  );

  if (inModal) return form;

  return (
    <MainCard
      title="New Booking"
      secondary={
        <Button
          variant={config.mode === 'dark' ? 'outline-light' : 'outline-dark'}
          className="back-btn d-flex align-items-center gap-2"
          onClick={() => navigate('/booking-management')}
        >
          <i className="ph ph-caret-left" style={{ fontSize: 18 }}></i>
          <span className="back-text">Back</span>
        </Button>
      }
    >
      <Card className="p-4 add-booking-card">{form}</Card>
    </MainCard>
  );
}

AddNewBooking.propTypes = {
  /** Drawn inside NewBookingModal rather than as its own page. */
  inModal: PropTypes.bool,
  /** Called after the booking is saved, in place of going back to the listing. */
  onCreated: PropTypes.func,
  /** The pop-up footer's Cancel. */
  onCancel: PropTypes.func
};
